import { z } from 'zod';
import { appCodeSchema, entityProfileSchema, entityRoleSchema, entitySlugSchema } from './claims.js';
import { roleGrantSchema } from './roles.js';
/** Destinatario de un aviso: por correo, por `sub` del id_token o por `uid` global. */
export const recipientSchema = z.union([z.object({ email: z.string().email() }), z.object({ sub: z.string() }), z.object({ uid: z.string().uuid() })]);
/** `POST /api/v1/service/notifications` */
export const pushNotificationsSchema = z.object({
    entitySlug: entitySlugSchema,
    recipients: z.array(recipientSchema).min(1).max(500),
    /** Código estable de la app (`TASK_ASSIGNED`, `CASE_ASSIGNED`…). */
    type: z.string().regex(/^[A-Z0-9_]+$/),
    title: z.string().min(1).max(200),
    body: z.string().max(2000).nullable().optional(),
    /** URL absoluta que abre la app donde corresponde (`https://planning.datalog.com.co/tareas/123`). */
    link: z.string().url().nullable().optional(),
    /** Idempotencia por app: repetir el mismo externalId no duplica el aviso. */
    externalId: z.string().max(200).optional(),
});
export const pushNotificationsResponseSchema = z.object({ created: z.number(), skipped: z.number(), unknownRecipients: z.array(z.string()) });
/** Lo que más apura de una persona en la app: lo vencido o lo que vence primero. */
export const pendingItemSchema = z.object({
    title: z.string().min(1).max(200),
    /** Frase corta del plazo: «Vencida ayer», «Vence hoy, 3:00 p. m.». */
    detail: z.string().max(120).nullable().optional(),
    /** URL absoluta a ese registro en la app. */
    link: z.string().url(),
    /** Vencimiento (ISO): la suite pone primero lo que vence antes entre todas las apps. */
    dueAt: z.string().datetime({ offset: true }).nullable().optional(),
});
/** Resumen de lo que espera a una persona en la app (Inicio de la suite, «Mi día»). */
export const pendingSummarySchema = z.object({
    recipient: recipientSchema,
    /** Cuántas cosas debe atender hoy (0 = al día). */
    total: z.number().int().min(0).max(1_000_000),
    /** Frase propia de la app: «4 tareas para hoy», «3 PQRS por responder». Sin ella, la suite dice «N pendientes». */
    text: z.string().max(120).nullable().optional(),
    urgent: pendingItemSchema.nullable().optional(),
    /** URL absoluta a la lista de pendientes en la app; sin ella, la suite abre la app. */
    link: z.string().url().nullable().optional(),
});
/** `PUT /api/v1/service/entities/:slug/pending` */
export const publishPendingSchema = z.object({
    people: z.array(pendingSummarySchema).max(5000),
    /** `true`: la lista es completa y quien no venga queda sin pendientes en la app (la publicación diaria). */
    replace: z.boolean().default(false),
    /** Hasta cuándo vale lo publicado (ISO). Sin él, 24 horas; vencido, la suite vuelve a los avisos sin leer. */
    validUntil: z.string().datetime({ offset: true }).nullable().optional(),
});
export const publishPendingResponseSchema = z.object({ updated: z.number(), cleared: z.number(), unknownRecipients: z.array(z.string()) });
/** `POST /api/v1/service/users/import` */
export const importUsersSchema = z.object({
    entitySlug: entitySlugSchema,
    users: z.array(z.object({
        email: z.string().email(),
        displayName: z.string().min(1).max(160),
        jobTitle: z.string().max(160).nullable().optional(),
        role: entityRoleSchema.default('MEMBER'),
        /** `sub`/id que la app ya usa para esa persona (se guarda como referencia). */
        externalRef: z.string().max(200).optional(),
        /** Roles que la persona ya tiene en la app (§3.9): la suite los guarda como los suyos para esa app. */
        roles: z.array(roleGrantSchema).optional(),
    })).min(1).max(5000),
    /** Dar acceso a la app que importa (normalmente sí). */
    grantApp: z.boolean().default(true),
    /** Enviar correo de invitación a los que no tenían cuenta en la suite. */
    invite: z.boolean().default(true),
    /**
     * Devolver el enlace de activación (`activationUrl`) de las cuentas creadas en esta llamada, para que la app lo
     * muestre y se comparta a mano (p. ej. cuando aún no hay correo). Nunca viene para una cuenta que ya existía.
     */
    activationLinks: z.boolean().default(false),
});
export const importUsersResponseSchema = z.object({
    created: z.number(),
    existing: z.number(),
    memberships: z.number(),
    invited: z.number(),
    users: z.array(z.object({ email: z.string(), uid: z.string(), sub: z.string(), created: z.boolean(), activationUrl: z.string().optional() })),
    /**
     * Personas que no se importaron: una persona pertenece a una sola entidad (`USER_IN_OTHER_ENTITY`) y las cuentas de
     * Datalog no pertenecen a ninguna (`SUPERADMIN_NO_ENTITIES`).
     */
    rejected: z.array(z.object({ email: z.string(), code: z.string(), message: z.string() })).default([]),
});
/** `GET /api/v1/service/entities/:slug` */
export const entityAppSchema = z.object({
    code: appCodeSchema,
    name: z.string(),
    /** Una línea con lo que hace («Proyectos, tareas y objetivos»), bajo el nombre en el lanzador de apps. */
    description: z.string().default(''),
    icon: z.string(),
    /** Índice 1–12 de la paleta categórica de `@xiltrion/ui`; `null` = uno estable sacado del código. */
    color: z.number().int().min(1).max(12).nullable().default(null),
    /** Logo oficial (SVG) servido por la suite; `null` = `LogoApp` lo dibuja con `icon` y `color`. */
    logoUrl: z.string().nullable().default(null),
    webUrl: z.string(),
    enabled: z.boolean(),
    modules: z.array(z.string()),
});
export const entityInfoSchema = z.object({
    slug: entitySlugSchema,
    name: z.string(),
    profile: entityProfileSchema,
    nit: z.string().nullable(),
    status: z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']),
    apps: z.array(entityAppSchema),
    branding: z.object({ color: z.string().nullable(), logoUrl: z.string().nullable() }),
    /** Panel de control de la entidad en la suite (`…/entidades/<slug>`): `panelControl` del `ShellApp`, solo para el ADMIN. */
    adminUrl: z.string().optional(),
});
/** `GET /api/v1/service/entities/:slug/users` */
export const entityUserSchema = z.object({ uid: z.string(), sub: z.string(), email: z.string(), displayName: z.string(), jobTitle: z.string().nullable(), role: entityRoleSchema, status: z.string() });
/** Aviso tal como lo devuelve la suite. */
export const suiteNotificationSchema = z.object({
    id: z.string(),
    app: appCodeSchema,
    appName: z.string(),
    entitySlug: entitySlugSchema,
    type: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    link: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * `GET /api/v1/service/entities/:slug/users/:uid/mfa`: si la persona tiene activa la app autenticadora en la suite
 * (§3.13). Se activa, se restablece y se quita solo en la suite.
 */
export const userMfaStatusSchema = z.object({ enabled: z.boolean() });
/**
 * `POST /api/v1/service/entities/:slug/users/:uid/mfa/verify`: la app comprueba un código de la app autenticadora de
 * la persona como segundo factor de una acción (firmar). Solo códigos de 6 dígitos (nunca los de recuperación) y cada
 * uno sirve una vez. `valid: false` también si la persona no la tiene activa.
 */
export const verifyUserMfaSchema = z.object({ code: z.string().trim().regex(/^\d{6}$/) });
export const verifyUserMfaResponseSchema = z.object({ valid: z.boolean() });
//# sourceMappingURL=service-api.js.map